import { useMemo } from 'react';
import currency from '|/helpers/currency';

// Both fraction digits must be passed together — `currency` forwards them to
// Intl.NumberFormat, which throws a RangeError if only the max is narrowed below the
// default min of 2.
const formattedCurrency = (amount: number) =>
  currency(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface UseWsysModalVmProps {
  tradeIncentive: number;
  taxSavings: number;
  stateName: string;
}

export interface SectionData {
  title: string;
  description: string;
  amount: string;
  label: string;
}

export interface CardData {
  type: 'bundle' | 'tax' | 'combined';
  sections: SectionData[];
}

export interface WsysModalVm {
  heroTitle: string;
  cards: CardData[];
}

export const useWsysModalVm = ({
  tradeIncentive,
  taxSavings,
  stateName
}: UseWsysModalVmProps): WsysModalVm => {
  // Format currency values
  const incentiveAmount = formattedCurrency(tradeIncentive);
  const taxSavingsAmount = formattedCurrency(taxSavings);
  const totalSavings = formattedCurrency(tradeIncentive + taxSavings);

  const hasIncentive = tradeIncentive > 0;
  const hasTaxSavings = taxSavings > 0;
  const hasBoth = hasIncentive && hasTaxSavings;

  // Determine hero title based on what savings they have
  const heroTitle = useMemo(() => {
    if (hasBoth) {
      return `Get ${totalSavings} more when you trade in`;
    }
    if (hasIncentive) {
      return `Get ${incentiveAmount} more when you trade in`;
    }
    return `Save ${taxSavingsAmount} in ${stateName} sales tax`;
  }, [hasBoth, hasIncentive, totalSavings, incentiveAmount, taxSavingsAmount, stateName]);

  // Build card data
  const cards = useMemo<CardData[]>(() => {
    const cardList: CardData[] = [];

    // When both incentive and tax savings are present, combine into one card
    if (hasBoth) {
      cardList.push({
        type: 'combined',
        sections: [
          {
            title: `Save ${incentiveAmount} when you trade in`,
            description: `Bundle your trade-in and get ${incentiveAmount} off your vehicle price from Carvana.`,
            amount: incentiveAmount,
            label: 'Bundle & Save discount'
          },
          {
            title: `Save ${taxSavingsAmount} in ${stateName} sales tax`,
            description: `Trading in reduces the ${stateName} sales tax you pay. There's no delay or paperwork to fill out — it's instant and automatic. You get a lower total purchase price and lower monthly payments.`,
            amount: taxSavingsAmount,
            label: 'Sales tax savings'
          }
        ]
      });
    } else {
      // Single card for incentive only
      if (hasIncentive) {
        cardList.push({
          type: 'bundle',
          sections: [
            {
              title: `Save ${incentiveAmount} when you trade in`,
              description: `Bundle your trade-in and get ${incentiveAmount} off your vehicle price from Carvana.`,
              amount: incentiveAmount,
              label: 'Bundle & Save discount'
            }
          ]
        });
      }

      // Single card for tax savings only
      if (hasTaxSavings) {
        cardList.push({
          type: 'tax',
          sections: [
            {
              title: `Save ${taxSavingsAmount} in ${stateName} sales tax`,
              description: `Trading in reduces the ${stateName} sales tax you pay. There's no delay or paperwork to fill out — it's instant and automatic. You get a lower total purchase price and lower monthly payments.`,
              amount: taxSavingsAmount,
              label: 'Sales tax savings'
            }
          ]
        });
      }
    }

    return cardList;
  }, [hasIncentive, hasTaxSavings, hasBoth, incentiveAmount, taxSavingsAmount, stateName]);

  return {
    heroTitle,
    cards
  };
};
